import React, { createContext, useState, useEffect, useMemo } from "react"
import { desktop, tablet, mobile } from "assets/styles/media"
import { addDebouncedEventListener, isBrowser } from "utils/functions"
import { graphql, useStaticQuery } from "gatsby"
import PageLoader from "components/PageLoader"

export const ScreenContext = createContext({
  fullWidth: false,
  desktop: false,
  tablet: false,
  mobile: false,
})

export const LoaderContext = createContext<{
  load: string
  setLoad: Function
  loaderIsReady: boolean
  setLoaderIsReady: Function
  lastPage?: string
  setLastPage?: Function
  canFinish?: boolean
  setCanFinish?: Function
  finished?: boolean
  setFinished?: Function
}>({
  load: "",
  setLoad: () => false,
  loaderIsReady: false,
  setLoaderIsReady: () => false,
  lastPage: "",
  setLastPage: () => "",
  canFinish: true,
  setCanFinish: () => true,
  finished: false,
  setFinished: () => false,
})

export const BannerContext = createContext<{
  bannerInfo: ContentfulBanner
  closeBanner: boolean
  setCloseBanner: Function
}>({
  bannerInfo: {
    bannerText: "",
    active: false,
    startDate: "",
    endDate: "",
    url: "",
  },
  closeBanner: false,
  setCloseBanner: () => false,
})

export const SubscribeFormOpenContext = createContext<{
  open: boolean
  setOpen: Function
}>({ open: false, setOpen: () => false })

interface props {
  children: React.ReactNode
  title?: string
  noFooter?: boolean
}

type ContentfulBanner = {
  bannerText: string
  active: boolean
  startDate: string
  endDate: string
  url: string
}

const Providers: React.FC<props> = ({ children }) => {
  const [fw, setFw] = useState<boolean>(false)
  const [d, setD] = useState<boolean>(false)
  const [t, setT] = useState<boolean>(false)
  const [m, setM] = useState<boolean>(false)
  const [load, setLoad] = useState<string>("")
  const [open, setOpen] = useState<boolean>(false)
  const [canFinish, setCanFinish] = useState<boolean>(true)
  const [finished, setFinished] = useState<boolean>(false)
  const [lastPage, setLastPage] = useState<string>("")
  const [loaderIsReady, setLoaderIsReady] = useState(false)
  const [closeBanner, setCloseBanner] = useState<boolean>(false)

  const data = useStaticQuery(graphql`
    {
      allContentfulBanner {
        nodes {
          bannerText
          active
          startDate
          endDate
          url
        }
      }
    }
  `)

  const allBanners = data.allContentfulBanner.nodes
  const today = new Date().getTime()

  const activeBanner = useMemo(
    () =>
      allBanners.filter((banner: ContentfulBanner) => {
        const startDateValue = new Date(banner.startDate).getTime()
        const endDateValue = new Date(banner.endDate).getTime()
        return banner.active && today >= startDateValue && today <= endDateValue
      }),
    [allBanners]
  )

  useEffect(() => {
    if (isBrowser()) {
      window.scrollTo(0, 0)

      const setScreenContext = () => {
        setM(window.innerWidth <= mobile)
        setT(window.innerWidth > mobile && window.innerWidth <= tablet)
        setD(window.innerWidth > tablet && window.innerWidth <= desktop)
        setFw(window.innerWidth > desktop)
      }

      setScreenContext()

      const removeListener = addDebouncedEventListener(
        window,
        "resize",
        setScreenContext,
        100
      )

      return removeListener
    }
  }, [])

  return (
    <ScreenContext.Provider
      value={{ fullWidth: fw, desktop: d, tablet: t, mobile: m }}
    >
      <SubscribeFormOpenContext.Provider value={{ open, setOpen }}>
        <LoaderContext.Provider
          value={{
            load,
            setLoad,
            loaderIsReady,
            setLoaderIsReady,
            lastPage,
            setLastPage,
            canFinish,
            setCanFinish,
            finished,
            setFinished,
          }}
        >
          <BannerContext.Provider
            value={{
              bannerInfo: activeBanner[0],
              closeBanner: closeBanner,
              setCloseBanner: setCloseBanner,
            }}
          >
            <PageLoader />
            {children}
          </BannerContext.Provider>
        </LoaderContext.Provider>
      </SubscribeFormOpenContext.Provider>
    </ScreenContext.Provider>
  )
}

export default Providers
